import { EqlFieldName } from '@modules/common/constants';
import {
  useFetchQuantifyFormulas,
  useFetchQuantifyReport,
  useShouldSupportAllFormulas,
  useTriggerQuantifyQueryAnalyticsEvent,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  QuantifyQuery,
  VisualisationType,
} from '@modules/quantify/store/types';
import { isAggregateFormula } from '@modules/quantify/utils';
import {
  Autocomplete,
  Box,
  Link,
  SxProps,
  TextField,
  Theme,
  Tooltip,
} from '@mui/material';
import { EqlField, EqlFieldValueType, Formula } from 'generated/graphql';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface MeasuredAsMetricAutocompleteProps {
  sx?: SxProps<Theme>;
}

export function MeasuredAsMetricAutocomplete({
  sx,
}: MeasuredAsMetricAutocompleteProps) {
  const shouldSupportAllFormulas = useShouldSupportAllFormulas();
  const shouldSkipPercentageFormula = useQuantifyStore(
    ({ visual }) => visual.shouldSkipPercentageFormula,
  );
  const [fetchQuantifyFormulas, { formulas: fetchedFormulas, isLoading }] =
    useFetchQuantifyFormulas();

  const formulas = useMemo(() => {
    if (shouldSkipPercentageFormula) {
      return fetchedFormulas.filter(
        (formula) => formula.eqlFunction !== EQLFunctionName.NormaliseByPercent,
      );
    }

    return fetchedFormulas;
  }, [shouldSkipPercentageFormula, fetchedFormulas]);

  const fetchQuantifyReportData = useFetchQuantifyReport();
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const groupBy = useQuantifyStore(({ query }) => query.groupBy);
  const whereClause = useQuantifyStore(
    ({ query }) => query.whereClauseConfig?.whereClause,
  );
  const compareTo = useQuantifyStore(({ query }) => query.compareTo);
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.measuredAsCriteria,
  );
  const [shouldFetchQuantifyReport, setShouldFetchQuantifyReport] =
    useState(false);
  const activeVisualisationType = useQuantifyStore(
    ({ metadata }) => metadata.activeVisualisationType,
  );
  const value = useQuantifyStore(
    ({ query }) =>
      formulas.find((formula) => formula.eqlFunction === query.formulaID) ||
      formulas[0],
  );

  const trackQuantifyQueryEvents = useTriggerQuantifyQueryAnalyticsEvent();

  useEffect(() => {
    groupBy?.field && fetchQuantifyFormulas(groupBy?.field);
  }, [fetchQuantifyFormulas, groupBy?.field]);

  useEffect(() => {
    if (shouldFetchQuantifyReport) {
      setShouldFetchQuantifyReport(false);
      fetchQuantifyReportData();
      trackQuantifyQueryEvents('Quantify - Query Executed');
    }
  }, [
    shouldFetchQuantifyReport,
    fetchQuantifyReportData,
    trackQuantifyQueryEvents,
  ]);

  function getUpdatedMeasuredAsCriteria(
    metricFormula: Formula,
  ): EqlField | null {
    switch (metricFormula.eqlFunction) {
      case EQLFunctionName.Count:
      case EQLFunctionName.NormaliseByPercent: {
        if (measuredAsCriteria?.valueType === EqlFieldValueType.None) {
          return measuredAsCriteria;
        }

        return null;
      }
      default: {
        if (measuredAsCriteria?.valueType === EqlFieldValueType.Number) {
          return measuredAsCriteria;
        }

        return null;
      }
    }
  }

  function onFormulaChange(_: SyntheticEvent, value: Formula | null) {
    if (!value) {
      return;
    }

    const queryPayload: Partial<QuantifyQuery> = {
      formulaID: value.eqlFunction as QuantifyQuery['formulaID'],
      measuredAsCriteria: getUpdatedMeasuredAsCriteria(value),
      denominatorWhereClause: whereClause,
    };

    if (compareTo?.type === 'whereClause') {
      queryPayload['compareTo'] = {
        ...compareTo,
        denominatorWhereClause: compareTo.whereClause ?? null,
      };
    }

    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: queryPayload,
    });

    setShouldFetchQuantifyReport(true);

    trackQuantifyQueryEvents('Quantify - Formula Selected', {
      measuredAsFormula: value.eqlFunction,
    });
  }

  const shouldDisablePercentageFormula =
    activeVisualisationType === VisualisationType.PIE;
  const shouldDisableAggregateFormulas =
    activeVisualisationType === VisualisationType.PIE;

  const getOptionTooltipTitle = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Formula,
  ) => {
    if (props['aria-disabled']) {
      if (
        shouldDisablePercentageFormula &&
        option.eqlFunction === EQLFunctionName.NormaliseByPercent
      ) {
        return 'This formula is not supported for the selected visualisation.';
      }

      if (
        shouldDisableAggregateFormulas &&
        isAggregateFormula(option.eqlFunction)
      ) {
        return 'This formula is not supported for the selected visualisation.';
      }

      const isNPSOrCSAT =
        option.eqlFunction === EQLFunctionName.NPS ||
        option.eqlFunction === EQLFunctionName.CSAT;

      const isFeedbackRecordsField =
        groupBy?.field === EqlFieldName.FeedbackRecords;

      if (isFeedbackRecordsField || (!option.isSupported && isNPSOrCSAT)) {
        return (
          <>
            <Box sx={{ whiteSpace: 'pre-line' }}>
              This formula is not supported for the selected field.
            </Box>
            <Link
              variant="subtitle1"
              underline="hover"
              href="https://helpcenter.enterpret.com/en/articles/8791835-quantify-query#h_4dc4d84073"
              target="_blank"
              sx={{ fontSize: 13 }}
            >
              Learn More
            </Link>
          </>
        );
      }
    }

    return (
      <>
        <Box sx={{ whiteSpace: 'pre-line' }}>{option.description}</Box>
        <Link
          href={option.helpCentreURL}
          target="_blank"
          variant="subtitle1"
          underline="hover"
          sx={{ fontSize: 13 }}
        >
          Learn More
        </Link>
      </>
    );
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Formula,
  ) => {
    return (
      <Tooltip
        arrow
        title={getOptionTooltipTitle(props, option)}
        placement="left"
        key={option.eqlFunction}
      >
        <Box>
          <Box component="li" {...props} sx={{ alignItems: 'flex-start' }}>
            {option.displayName}
          </Box>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Autocomplete
      id="quantify-measured-as-metric"
      autoHighlight
      disableClearable
      clearIcon={null}
      disabled={!groupBy}
      value={value ?? null}
      options={formulas}
      loading={isLoading}
      groupBy={(option) => option.group}
      onChange={onFormulaChange}
      renderInput={(params) => <TextField {...params} label="" />}
      renderOption={renderOption}
      getOptionLabel={(option) => option.displayName}
      isOptionEqualToValue={(option, value) =>
        option.eqlFunction === value?.eqlFunction
      }
      getOptionDisabled={(option) => {
        if (
          shouldDisablePercentageFormula &&
          option.eqlFunction === EQLFunctionName.NormaliseByPercent
        ) {
          return true;
        }

        if (groupBy?.field === EqlFieldName.FeedbackRecords) {
          if (
            option.eqlFunction === EQLFunctionName.Count ||
            option.eqlFunction === EQLFunctionName.NormaliseByPercent
          ) {
            return false;
          }
          return true;
        }

        if (
          shouldDisableAggregateFormulas &&
          isAggregateFormula(option.eqlFunction)
        ) {
          return true;
        }

        if (shouldSupportAllFormulas) {
          return false;
        }

        // ##TODO - Remove this check once we handle this in the backend
        // Consider isSupported only for NPS and CSAT
        if (
          !option.isSupported &&
          (option.eqlFunction === EQLFunctionName.NPS ||
            option.eqlFunction === EQLFunctionName.CSAT)
        ) {
          return true;
        }

        return false;
      }}
      sx={{ ...sx }}
      componentsProps={{
        popper: { sx: { minWidth: '320px' }, placement: 'bottom-start' },
      }}
    />
  );
}
